import {
  FC,
  PropsWithChildren,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { PageContext, PageValues } from '../model/PageContext';

/**
 * @description PageMetaProvider - Simple optimized version
 *
 * Optimizations:
 * - setValues is stable (uses useRef to access latest values)
 * - Context value only changes when values change
 * - Volatile fields are auto-cleared when title changes (prevents stale UI from previous pages)
 */

// Fields that should be reset when navigating to a new page (when title changes)
const VOLATILE_FIELDS: (keyof PageValues)[] = [
  'descriptionElement',
  'versionSelector',
  'additional',
  'backButton',
  'backButtonAction',
  'backNavigationLabel',
];

const PageMetaProvider: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const prevPathnameRef = useRef<string | null>(null);

  const [values, setValuesState] = useState<PageValues>({
    loading: false,
    fullPage: false,
    generateModalOpened: false,
    withBorder: false,
    disablePadding: false,
    versionsModalOpened: false,
    resultModalOpened: false,
    chatSourceModalOpened: false,
    depositionSummarySourceModalOpened: false,
    groundXModalOpened: false,
    generateTemplateModalOpened: false,
    generateDepositionSummaryModalOpened: false,
    generateUseCaseModalOpened: false,
  });

  // Use ref to always have access to latest values without recreating setValues
  const valuesRef = useRef(values);
  valuesRef.current = values;

  /**
   * Route-change reset:
   * Clear page-specific "header chrome" fields whenever the route pathname changes.
   *
   * This prevents leakage like: UseCaseView sets `additional` (Landscape/Editor/Download),
   * then navigating to Create page still shows it if Create page doesn't override/clear it.
   *
   * We intentionally do NOT reset global modal flags here.
   */
  useEffect(() => {
    const pathname = location?.pathname ?? '';
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;

    setValuesState((prev) => {
      const next: PageValues = { ...prev };

      // Reset common page-level UI state
      next.title = undefined;
      next.description = undefined;
      next.descriptionElement = undefined;
      next.tooltipText = undefined;
      next.tooltipQuestionTitle = undefined;
      next.additional = undefined;
      next.versionSelector = undefined;
      next.searchValue = undefined;

      // Reset layout flags to defaults
      next.loading = false;
      next.fullPage = false;
      next.withBorder = false;
      next.disablePadding = false;
      next.hideTop = false;

      // Reset back button state
      next.backButton = false;
      next.backButtonAction = undefined;
      next.backNavigationLabel = undefined;

      return next;
    });
  }, [location?.pathname]);

  // ✅ Stable setValues function - never recreates
  const setValues = useCallback((update: any) => {
    if (typeof update === 'function') {
      // Functional update: call with current values from ref
      setValuesState((prev) => {
        const newValues = update(prev);

        // If title is changing, clear volatile fields to prevent stale UI
        const isTitleChanging =
          'title' in newValues && newValues.title !== prev.title;

        if (isTitleChanging) {
          // Create a clean slate for volatile fields
          const cleanedPrev = { ...prev };
          VOLATILE_FIELDS.forEach((field) => {
            // Only clear if not explicitly set in the new values
            if (!(field in newValues)) {
              cleanedPrev[field] = undefined;
            }
          });
          return { ...cleanedPrev, ...newValues };
        }

        return { ...prev, ...newValues };
      });
    } else {
      // Direct update: merge with current values
      setValuesState((prev) => {
        // If title is changing, clear volatile fields
        const isTitleChanging =
          'title' in update && update.title !== prev.title;

        if (isTitleChanging) {
          const cleanedPrev = { ...prev };
          VOLATILE_FIELDS.forEach((field) => {
            if (!(field in update)) {
              cleanedPrev[field] = undefined;
            }
          });
          return { ...cleanedPrev, ...update };
        }

        return { ...prev, ...update };
      });
    }
  }, []); // Empty deps = stable function reference

  // ✅ Memoize context value - only changes when values change
  // setValues is stable, so it won't cause extra re-renders
  const contextValue = useMemo(
    () => ({ values, setValues }),
    [values, setValues]
  );

  return (
    <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>
  );
};

export default PageMetaProvider;
